import { useState } from "react";
import { ChevronDown, Plus, Minus } from "lucide-react";

/**
 * CollapsibleSection — wraps a Landing-page section with a clickable header.
 *
 * Props:
 *   id          — anchor id (used by SectionJumpNav links)
 *   title       — bold section heading shown in the header bar
 *   subtitle    — small descriptor text shown next to the title
 *   defaultOpen — initial open state (default false)
 *   icon        — optional lucide icon to display left of the title
 *   children    — actual section content (rendered when expanded)
 *   testId      — optional data-testid prefix for the section
 */
export default function CollapsibleSection({
  id,
  title,
  subtitle,
  defaultOpen = false,
  icon: Icon,
  children,
  testId,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      id={id}
      data-testid={testId || `collapsible-${id}`}
      className="border-b border-zinc-200/70 bg-white/40 backdrop-blur-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testId || `collapsible-${id}`}-toggle`}
        className="w-full max-w-7xl mx-auto px-6 lg:px-10 py-5 lg:py-6 flex items-center gap-4 hover:bg-white/60 transition-colors text-left group"
      >
        {Icon ? (
          <span
            className={`inline-flex items-center justify-center w-9 h-9 border border-zinc-300 transition-colors ${
              open ? "bg-[#EF4444] text-white border-[#EF4444]" : "bg-white text-zinc-700"
            }`}
          >
            <Icon className="w-4 h-4" />
          </span>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="font-display font-bold text-xl lg:text-2xl text-zinc-900 leading-tight">
              {title}
            </h2>
            {subtitle ? (
              <span className="text-xs lg:text-sm text-zinc-500 font-mono">{subtitle}</span>
            ) : null}
          </div>
        </div>
        <span
          className={`inline-flex items-center justify-center w-7 h-7 border border-zinc-300 transition-all ${
            open ? "bg-zinc-900 text-white border-zinc-900 rotate-0" : "bg-white text-zinc-700"
          }`}
          aria-hidden="true"
        >
          {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-500 ease-out ${
          open ? "max-h-[6000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {children}
      </div>
    </section>
  );
}
